import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { logging } from 'helpers/monitoring';
import { combineLatest } from 'rxjs';

import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { StoreService } from 'app/b2b-order/services/store.service';
import { AddressModel } from 'app/order-management/retailer-platform/manage-stores/manage-stores.types';
import { glnPattern, shortNameUniquenessValidator } from 'app/order-management/retailer-platform/manage-stores/shared';
import { Backdrop, ModalOptions, Position, Size } from 'app/shared/components/modal/types';
import * as _ from 'lodash';
import { Branch, OrganizationModel } from 'models';
import { BranchToCreate } from 'models/organization.model';
import { ModalService, SegmentService } from 'services';

@Component({
    selector: 'fc-create-store-dialog',
    templateUrl: './create-store-dialog.component.html',
    styleUrls: ['./create-store-dialog.component.less'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CreateStoreDialogComponent implements OnInit, OnDestroy {
    @Input() modalId: string;
    @Input() accept = 'TEXT_SAVE';
    @Input() cancel = 'TEXT_CANCEL';
    @Input() options: ModalOptions = {
        size: Size.Form,
        backdrop: Backdrop.Static,
        position: Position.Center,
        showCancelButton: true
    };

    @Output() onReject = new EventEmitter<void>();
    @Output() onAccept = new EventEmitter<void>();

    public branch: Branch;
    public branchForm: UntypedFormGroup;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private segmentService: SegmentService,
        private organizationModel: OrganizationModel,
        protected storeService: StoreService,
        private modalService: ModalService
    ) {
        this.branch = { address: {} } as Branch;
    }

    ngOnInit() {
        combineLatest([this.storeService.storesIncludingDisabled]).subscribe(([stores]) => {
            this.createBranchForm(stores);
        });
    }

    ngOnDestroy(): void {
        this.storeService.resetHasError();
    }

    get addressForm(): Omit<AbstractControl, 'value'> & { value: AddressModel } {
        return this.branchForm.get('addressForm');
    }

    get glnForm() {
        return this.branchForm.get('glnForm');
    }

    get isFormValid() {
        return this.branchForm.valid && !this.branchForm.disabled;
    }

    public submitBranch() {
        this.storeService
            .createStore(this.buildBranch())
            .then(() => {
                this.segmentService.track('Create store');
                this.clearFormInputs();
                this.onAccept.emit();
                this.close();
            })
            .catch((error) => {
                logging.error('Error creating store', { error });
                this.onServerError(error);
            });
    }

    public close() {
        this.modalService.close(this.modalId);
    }

    private createBranchForm(stores: Branch[]) {
        this.branchForm = this.formBuilder.group({
            addressForm: this.formBuilder.group({
                name: [''],
                shortName: ['', [shortNameUniquenessValidator(stores), Validators.maxLength(16)]],
                address1: ['', Validators.required],
                city: ['', Validators.required],
                country: ['DE', Validators.required],
                phone: ['', Validators.required],
                zip: ['', Validators.required]
            }),
            glnForm: this.formBuilder.group({
                useFcGln: [false, Validators.required],
                gln: [
                    '',
                    [Validators.required, Validators.pattern(glnPattern)],
                    (control) => this.existingGlnValidator(control)
                ],
                fcGln: [''],
                buyerGln: ['', Validators.pattern(glnPattern)],
                billingGln: ['', Validators.pattern(glnPattern)]
            })
        });

        this.glnForm.get('useFcGln').valueChanges.subscribe((useFcGln) => {
            const glnControl = this.glnForm.get('gln');
            const buyerGlnControl = this.glnForm.get('buyerGln');
            const billingGlnControl = this.glnForm.get('billingGln');

            if (useFcGln) {
                buyerGlnControl.setValue('');
                billingGlnControl.setValue('');
                glnControl.setValue('');
                glnControl.disable();
                buyerGlnControl.disable();
                billingGlnControl.disable();
            } else {
                glnControl.enable();
                buyerGlnControl.enable();
                billingGlnControl.enable();
            }
        });
    }

    private clearFormInputs() {
        this.branchForm.reset();
        this.addressForm.get('country').setValue('DE');
        this.glnForm.get('useFcGln').setValue(false);
    }

    private buildBranch(): BranchToCreate {
        const name = this.addressForm.value.name;
        const phone = this.addressForm.value.phone;
        const gln = this.glnForm.get('gln').value;
        const useFcGln = this.glnForm.get('useFcGln').value;
        const address = _.omit(_.assign(this.addressForm.value, { gln }), ['name', 'phone']);

        const buyerGln = this.glnForm.get('buyerGln').value;
        const billingGln = this.glnForm.get('billingGln').value;

        const branch = {
            address,
            name,
            shortName: this.addressForm.value.shortName,
            phone,
            useFcGln,
            buyerAddress: !useFcGln && !_.isEmpty(buyerGln) ? { gln: buyerGln } : null,
            billingAddress: !useFcGln && !_.isEmpty(billingGln) ? { gln: billingGln } : null
        };

        return branch;
    }

    private async existingGlnValidator(glnFormControl: AbstractControl) {
        const gln = _.toString(glnFormControl.value);
        // Only run async validation when value has changed
        if (gln === this.branch.address.gln) {
            return Promise.resolve(null);
        }

        try {
            const branchesGln = await this.organizationModel.getAllBranches({ 'address.gln': gln });
            const branchesFcGln = await this.organizationModel.getAllBranches({ 'address.fcGln': gln });

            const branches = _.concat(branchesGln, branchesFcGln);
            const isAvailableGln = !_.find(branches, (branch) => branch.address.gln === gln);
            return isAvailableGln ? null : { existingGln: { value: glnFormControl.value } };
        } catch (error) {
            this.onServerError(error);
        }
    }

    private onServerError(error) {
        this.branchForm.disable();
        this.branchForm.setErrors({ serverError: error });
    }
}
